/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import { useOutletContext } from 'react-router';
import { Popover } from '@mui/material';
import Select from 'react-select';
import { FLEET_AI_ENABLED, REPORTS_ENABLED } from '../../../config/featureFlags';
import MenuPopup from '../../../component/menuPopup/MenuPopup';
import VerticalDots from '../../../component/table/img/verticalDots.svg';
import ConfigTab from '../../../component/configurationTab/configurationTab';
import Title from '../../../component/title/Title';
import ColoredLine from '../../../component/coloredLine/ColoredLine';
import Checkbox from '../../../component/checkbox/Checkbox';
import {
    SecondaryButton, PrimaryButton, BackButton, CreateWithIcon, PreviousButton, NextButton,
} from '../../../component/buttonComponent/ButtonComponent';
import SnackBar from '../../../component/snackbar/Snackbar';
import HorizonModal from '../../../component/modal/Modal';
import '../../../component/modal/modal.css';
import {
    APIGetAutomateReports,
    APIPostAutomateReport,
    APIPatchAutomateReport,
    APIDeleteAutomateReport,
    APIGetDevices,
    APIGetV3TeamMembers,
} from '../../../config/restAPI/FetchAPI';
import '../notifications/notifications.css';
import './Report.css';

const DEFAULT_SEND_TIME = '09:00';

/** Devices API page size per request; pages are merged until `totalData` is satisfied (see `fetchDevices`). */
const DEVICES_FETCH_PAGE_SIZE = 500;
const MAX_DEVICE_FETCH_PAGES = 1000;

/** Team `/team` API page size; pages merged until `totalData` is satisfied (see `fetchTeam`). */
const TEAMS_FETCH_PAGE_SIZE = 200;
const MAX_TEAM_FETCH_PAGES = 500;

const deviceRecordToSelectOption = (d) => {
    const serial = (d.deviceSerial || '').trim();
    const asset = (d.assetName || '').trim();
    const label = asset || serial || 'Device';
    return { value: d.oid, label, serial };
};

const isValidTimeHHmm = (s) => {
    if (typeof s !== 'string' || !/^\d{1,2}:\d{2}$/.test(s)) return false;
    const [h, m] = s.split(':').map(Number);
    return h >= 0 && h <= 23 && m >= 0 && m <= 59;
};

const SCHEDULE_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
];

const WEEKLY_REPEAT_OPTIONS = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' },
];

const MONTHLY_DOM_LAST = 'last';

const ordinalDayLabel = (n) => {
    const j = n % 10;
    const k = n % 100;
    if (k >= 11 && k <= 13) return `${n}th`;
    if (j === 1) return `${n}st`;
    if (j === 2) return `${n}nd`;
    if (j === 3) return `${n}rd`;
    return `${n}th`;
};

const MONTHLY_REPEAT_OPTIONS = [
    ...Array.from({ length: 28 }, (_, i) => {
        const d = i + 1;
        return {
            value: String(d),
            label: `${ordinalDayLabel(d)} day of the month`,
        };
    }),
    { value: MONTHLY_DOM_LAST, label: 'Last day of the month' },
];

const HOURLY_TIME_OPTIONS = Array.from({ length: 24 }, (_, h) => {
    const hh = String(h).padStart(2, '0');
    const value = `${hh}:00`;
    return { value, label: moment(value, 'HH:mm').format('h:mm A') };
});

const normalizeTimeToHour = (timeStr) => {
    if (!isValidTimeHHmm(timeStr)) return DEFAULT_SEND_TIME;
    const [h] = timeStr.split(':').map(Number);
    return `${String(h).padStart(2, '0')}:00`;
};

/** Next calendar date (YYYY-MM-DD) for PATCH body — API still expects this on update. */
const nextDateStringForIsoWeekday = (isoWeekday) => {
    const target = Number(isoWeekday);
    const m = moment().startOf('day');
    const delta = (target - m.isoWeekday() + 7) % 7;
    return m.clone().add(delta, 'days').format('YYYY-MM-DD');
};

const monthlyScheduleSendDate = (domKey) => {
    if (domKey === MONTHLY_DOM_LAST) {
        return moment().endOf('month').format('YYYY-MM-DD');
    }
    const day = parseInt(domKey, 10);
    if (Number.isNaN(day) || day < 1 || day > 28) {
        return moment().startOf('day').date(1).format('YYYY-MM-DD');
    }
    return moment().startOf('day').date(day).format('YYYY-MM-DD');
};

const formatMonthlySummaryFragment = (domKey) => {
    if (domKey === MONTHLY_DOM_LAST) return 'Last day of the month';
    const n = parseInt(domKey, 10);
    if (Number.isNaN(n)) return '-';
    return `${ordinalDayLabel(n)} day of the month`;
};

/** API: "monday"…"sunday" or "0"–"6" (0=Sunday). UI: moment isoWeekday 1=Mon … 7=Sun. */
const WEEKDAY_NAME_LOWER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const isoWeekdayToApiDayName = (iso) => {
    const n = Number(iso);
    if (n >= 1 && n <= 7) return WEEKDAY_NAME_LOWER[n - 1];
    return 'monday';
};

const isoWeekdayFromApiWeeklyRepeat = (raw) => {
    if (raw == null || raw === '') return WEEKLY_REPEAT_OPTIONS[0].value;
    const s = String(raw).trim().toLowerCase();
    const nameIdx = WEEKDAY_NAME_LOWER.indexOf(s);
    if (nameIdx !== -1) return nameIdx + 1;
    const n = typeof raw === 'number' ? raw : parseInt(s, 10);
    if (!Number.isNaN(n)) {
        if (n >= 0 && n <= 6) {
            return n === 0 ? 7 : n;
        }
        if (n >= 1 && n <= 7) return n;
    }
    return WEEKLY_REPEAT_OPTIONS[0].value;
};

/** Only attach agencyOid on create when cookie/context is a real 24-hex Mongo id (admin / token agency id). */
const isMongoObjectIdString = (s) => typeof s === 'string' && /^[a-f\d]{24}$/i.test(s);

const normalizeMonthlyRepeatKeyFromApi = (raw) => {
    if (raw == null || raw === '') return '1';
    if (raw === MONTHLY_DOM_LAST || String(raw).toLowerCase() === 'last') {
        return MONTHLY_DOM_LAST;
    }
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 28) return String(n);
    return '1';
};

const unwrapMongoDate = (val) => {
    if (val == null) return null;
    if (typeof val === 'string' || typeof val === 'number') return val;
    if (typeof val === 'object' && val.$date !== undefined) {
        const inner = val.$date;
        if (inner && typeof inner === 'object' && inner.$numberLong !== undefined) {
            const ms = parseInt(inner.$numberLong, 10);
            return Number.isNaN(ms) ? null : ms;
        }
        return inner;
    }
    return val;
};

const pickNextScheduleSendSource = (item) => {
    if (!item || typeof item !== 'object') return null;
    return item.nextScheduleSendDate
        ?? item.nextRunAt
        ?? item.next_schedule_send_date
        ?? null;
};

const formatNextScheduleSendLabel = (val) => {
    const raw = unwrapMongoDate(val);
    if (raw == null || raw === '') return '—';
    const m = moment(raw);
    return m.isValid() ? m.format('DD MMM YYYY, h:mm A') : '—';
};

const getSelectedReportTypes = (config = {}) => {
    const reportTypes = [];
    const tel = config.telemetryLog;
    const od = config.odAnalytic;
    if (tel) reportTypes.push('Telemetry Log');
    if (od) reportTypes.push('O/D Analytic');
    return reportTypes;
};

const coerceToBooleanOrNull = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') {
        if (v === 0) return false;
        if (v === 1) return true;
        return null;
    }
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'true' || s === '1' || s === 'yes') return true;
        if (s === 'false' || s === '0' || s === 'no') return false;
        return null;
    }
    return null;
};

const pickReportEnabledFromRaw = (item) => {
    if (!item || typeof item !== 'object') return true;
    if (item.isDisabled === true || item.disabled === true) return false;
    const keys = [
        'isEnabled', 'enabled', 'active', 'isActive', 'is_active', 'is_enabled',
        'reportEnabled', 'isReportEnabled',
    ];
    for (let i = 0; i < keys.length; i += 1) {
        const t = coerceToBooleanOrNull(item[keys[i]]);
        if (t !== null) return t;
    }
    return true;
};

/** CREATE/PATCH payloads — camelCase + snake_case so gateways/schemas that only map one form still receive the flag. */
const automateReportEnabledBodyFields = (enabled) => ({
    isEnabled: enabled,
});

/**
 * Reference — Alert Configurations “Enabled” (same idea as top-level `isEnabled` on `alertPolicy` in MongoDB):
 *
 * - Toggle/API: `horizon-frontend/src/component/table/column/ColumnNotification.js` → `policiesStatus`:
 *   `axios.patch(\`${urlV3}/alertPolicy\`, { alertPolicy: { oid: row.oid, isEnabled: !row.isEnabled } })`
 *   via `APIPatchAlertPolicy` in `horizon-frontend/src/config/restAPI/PatchAPI.js`.
 * - List mapping from GET: `horizon-frontend/src/panel/notifications/Configuration/NotificationConfiguration.js`
 *   maps `isEnabled: x.isEnabled` into table rows.
 *
 * Automated reports should behave the same in Mongo (boolean `isEnabled` on each doc).
 */

/** Grey pills in the list table (same pill pattern as Team access privileges, neutral palette). */
const ReportTypesChips = ({ labels }) => {
    if (!labels || labels.length === 0) {
        return <span className="reports-report-types-empty">—</span>;
    }
    return (
        <div className="reports-report-types-chips">
            {labels.map((label) => (
                <span key={label} className="reports-report-type-chip">{label}</span>
            ))}
        </div>
    );
};

const formatScheduleSummary = (config) => {
    if (!config) return '-';
    const scheduleLabel = SCHEDULE_OPTIONS.find((o) => o.value === config.schedule)?.label
        || (config.schedule ? String(config.schedule) : null)
        || 'Weekly';
    const timeLabel = isValidTimeHHmm(config.sendTime)
        ? moment(normalizeTimeToHour(config.sendTime), 'HH:mm').format('h:mm A')
        : moment(DEFAULT_SEND_TIME, 'HH:mm').format('h:mm A');
    if (config.schedule === 'daily') {
        return `${scheduleLabel} · ${timeLabel}`;
    }
    if (config.schedule === 'monthly') {
        const mk = normalizeMonthlyRepeatKeyFromApi(config.monthlyRepeatDay);
        return `${scheduleLabel} · ${formatMonthlySummaryFragment(mk)} · ${timeLabel}`;
    }
    const n = isoWeekdayFromApiWeeklyRepeat(config.weeklyRepeatDay);
    const opt = WEEKLY_REPEAT_OPTIONS.find((o) => o.value === n);
    const dayLabel = opt ? opt.label : '-';
    return `${scheduleLabel} · ${dayLabel} · ${timeLabel}`;
};

const resolveHydrateScheduleKind = (rawSchedule) => {
    if (rawSchedule === 'monthly') return 'monthly';
    if (rawSchedule === 'daily') return 'daily';
    return 'weekly';
};

const getScheduleTimeSupportingText = (scheduleValue) => {
    if (scheduleValue === 'weekly') {
        return 'Repeat each week on your chosen day (report data covers the prior 7 days) and pick an hourly send time.';
    }
    if (scheduleValue === 'monthly') {
        return 'Repeat each month on a fixed day (1st–28th) or the last day of the month, and pick an hourly send time.';
    }
    return 'Send every calendar day at your chosen hourly time.';
};

const extractAutomateReportsList = (result) => {
    if (!result || result.response) return [];
    const body = result.data;
    if (!body) return [];
    if (body.status === 200 && body.data !== undefined) {
        if (Array.isArray(body.data)) return body.data;
        if (body.data && typeof body.data === 'object') return [body.data];
        return [];
    }
    if (Array.isArray(body.data)) return body.data;
    if (body.data && typeof body.data === 'object') return [body.data];
    if (Array.isArray(body)) return body;
    return [];
};

const pickApiErrorMessage = (result) => {
    const m = result?.response?.data?.message ?? result?.data?.message;
    if (m && typeof m === 'string') return m;
    return 'Something went wrong. Please try again.';
};

const isAutomateReportApiSuccess = (result) => {
    if (!result || result.response) return false;
    const s = result.data?.status ?? result.status;
    return s === 200 || s === 201 || s === 204;
};

/** PATCH/DELETE URL id — prefer Mongo ObjectId, not agency `oid` UUID. */
const reportRowId = (item) => {
    if (!item || typeof item !== 'object') return 'unknown';
    // eslint-disable-next-line no-underscore-dangle -- MongoDB document key
    const mongoId = item._id;
    if (mongoId && typeof mongoId === 'object' && mongoId.$oid !== undefined) {
        return String(mongoId.$oid);
    }
    if (mongoId) return String(mongoId);
    if (item.id != null && item.id !== '') return String(item.id);
    if (item.oid) return String(item.oid);
    return String(item.name || 'report');
};

const mapApiReportToRow = (item) => {
    const id = reportRowId(item);
    const updatedAtVal = item.updatedAt;
    let updatedRaw = updatedAtVal;
    if (updatedAtVal && typeof updatedAtVal === 'object' && updatedAtVal.$date !== undefined) {
        updatedRaw = updatedAtVal.$date;
    }
    const updatedAt = updatedRaw
        ? moment(updatedRaw).format('DD MMM YYYY, h:mm A')
        : '-';
    const notifyCount = Array.isArray(item.notifyUserIds) ? item.notifyUserIds.length : 0;
    const reportTypeTags = getSelectedReportTypes(item);
    return {
        id,
        raw: item,
        name: (item.name || '').trim() || 'Automated Report',
        description: typeof item.description === 'string' ? item.description : '',
        reportTypeTags,
        schedule: formatScheduleSummary(item),
        nextSend: formatNextScheduleSendLabel(pickNextScheduleSendSource(item)),
        notifiedUsers: notifyCount,
        updatedAt,
        reportEnabled: pickReportEnabledFromRaw(item),
    };
};

const normalizeReportItemsToRows = (items) => {
    let list = [];
    if (Array.isArray(items)) {
        list = items;
    } else if (items && typeof items === 'object') {
        list = [items];
    }
    return list
        .filter((r) => r && !r.isDeleted)
        .map((item) => mapApiReportToRow(item));
};

const navigationList = [
    {
        text: 'Profile',
        value: 1,
        path: '/settings/profile',
        enabled: true,
    },
    {
        text: 'Security',
        value: 2,
        path: '/settings/security',
        enabled: true,
    },
    {
        text: 'Team',
        value: 3,
        path: '/settings/teams',
        enabled: true,
    },
    {
        text: 'Notifications',
        value: 4,
        path: '/settings/notifications',
        enabled: true,
    },
    {
        text: 'Fleet AI',
        value: 5,
        path: '/settings/fleets',
        enabled: FLEET_AI_ENABLED,
    },
    {
        text: 'Reports',
        value: 6,
        path: '/settings/reports',
        enabled: REPORTS_ENABLED,
    },
];

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '2.75rem',
        borderColor: '#D0D5DD',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        fontSize: '0.875rem',
    }),
    menu: (base) => ({ ...base, zIndex: 20 }),
    multiValue: (base) => ({ ...base, backgroundColor: '#F2F4F7' }),
};

const Reports = (props) => {
    const { totalUsers } = props;
    const [navigtionClick, setNavigtionClick, , setNavigationState] = useOutletContext();

    const token = Cookies.get('jwtToken');
    const agencyID = Cookies.get('agencyUUID');

    const [devicesReady, setDevicesReady] = useState(false);
    const [teamReady, setTeamReady] = useState(false);

    const [telemetryLog, setTelemetryLog] = useState(true);
    const [odAnalytic, setOdAnalytic] = useState(false);
    const [reportName, setReportName] = useState('');
    const [reportDescription, setReportDescription] = useState('');

    const [deviceOptions, setDeviceOptions] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);

    const [schedule, setSchedule] = useState(SCHEDULE_OPTIONS[0]);
    const [reportEnabled, setReportEnabled] = useState(true);

    const [userOptions, setUserOptions] = useState([]);
    const [notifiedUsers, setNotifiedUsers] = useState([]);

    const [weeklyRepeatDay, setWeeklyRepeatDay] = useState(WEEKLY_REPEAT_OPTIONS[0]);
    const [monthlyRepeatDay, setMonthlyRepeatDay] = useState(MONTHLY_REPEAT_OPTIONS[0]);
    const [scheduleSendTime, setScheduleSendTime] = useState(DEFAULT_SEND_TIME);

    const [displaySnackbar, setDisplaySnackbar] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [reportsList, setReportsList] = useState([]);
    const [reportsListLoading, setReportsListLoading] = useState(false);
    const [reportsListPage, setReportsListPage] = useState(1);
    const [editingReportId, setEditingReportId] = useState(null);
    const [editingBaselineRaw, setEditingBaselineRaw] = useState(null);
    const [saveSubmitting, setSaveSubmitting] = useState(false);
    const [snackbarTitle, setSnackbarTitle] = useState('');
    const [snackbarSubtitle, setSnackbarSubtitle] = useState('');
    const [snackbarAction, setSnackbarAction] = useState('success');
    const [deleteReportModalOpen, setDeleteReportModalOpen] = useState(false);
    const [reportPendingDelete, setReportPendingDelete] = useState(null);
    const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
    const [reportMenuRowId, setReportMenuRowId] = useState(null);

    const lastDeviceHydrateKeyRef = useRef('');
    const lastNotifyHydrateKeyRef = useRef('');
    /** When true, deferred hydration must not overwrite the user's device multi-select. */
    const deviceSelectionTouchedByUserRef = useRef(false);
    /** When true, deferred hydration must not overwrite the user's notify-users multi-select. */
    const notifySelectionTouchedByUserRef = useRef(false);

    const fetchDevices = async () => {
        try {
            const accumulated = [];
            let pageNumber = 1;
            let totalExpected = null;

            while (pageNumber <= MAX_DEVICE_FETCH_PAGES) {
                const params = { pageNumber, totalPerPage: DEVICES_FETCH_PAGE_SIZE };
                /* Sequential requests: next page depends on prior batch length / totalData. */
                // eslint-disable-next-line no-await-in-loop -- paginated merge until all devices loaded
                const result = await APIGetDevices(() => {}, token, agencyID, params);
                if (result?.data?.status !== 200) break;

                const { data } = result.data;
                const batch = Array.isArray(data) ? data : [];
                if (typeof result.data.totalData === 'number' && !Number.isNaN(result.data.totalData)) {
                    totalExpected = result.data.totalData;
                }

                accumulated.push(...batch);

                if (batch.length === 0) break;
                if (totalExpected != null && accumulated.length >= totalExpected) break;
                if (batch.length < DEVICES_FETCH_PAGE_SIZE) break;

                pageNumber += 1;
            }

            setDeviceOptions(accumulated.map(deviceRecordToSelectOption));
        } catch (e) {
            setDeviceOptions([]);
        } finally {
            setDevicesReady(true);
        }
    };

    const fetchTeam = async () => {
        try {
            const accumulated = [];
            let pageNumber = 1;
            let totalExpected = null;

            while (pageNumber <= MAX_TEAM_FETCH_PAGES) {
                const params = { pageNumber, totalPerPage: TEAMS_FETCH_PAGE_SIZE };
                /* Sequential requests: next page depends on prior batch length / totalData. */
                // eslint-disable-next-line no-await-in-loop -- paginated merge until all team members loaded
                const result = await APIGetV3TeamMembers(() => {}, token, agencyID, params);
                if (result?.data?.status !== 200) break;

                const { data } = result.data;
                const batch = Array.isArray(data) ? data : [];
                if (typeof result.data.totalData === 'number' && !Number.isNaN(result.data.totalData)) {
                    totalExpected = result.data.totalData;
                }

                accumulated.push(...batch);

                if (batch.length === 0) break;
                if (totalExpected != null && accumulated.length >= totalExpected) break;
                if (batch.length < TEAMS_FETCH_PAGE_SIZE) break;

                pageNumber += 1;
            }

            const opts = accumulated
                .filter((m) => m.userEmail)
                .map((m) => ({
                    value: m.oid,
                    label: m.userEmail,
                }));
            setUserOptions(opts);
        } catch (e) {
            setUserOptions([]);
        } finally {
            setTeamReady(true);
        }
    };

    const fetchAutomateReports = useCallback(async (options = {}) => {
        const silent = Boolean(options.silent);
        if (!token || !agencyID) return;
        if (!silent) setReportsListLoading(true);
        try {
            const result = await APIGetAutomateReports(token, agencyID);
            if (result?.response) {
                if (!silent) setReportsList([]);
                return;
            }
            const items = extractAutomateReportsList(result);
            setReportsList(normalizeReportItemsToRows(items));
        } catch (e) {
            if (!silent) setReportsList([]);
        } finally {
            if (!silent) setReportsListLoading(false);
        }
    }, [token, agencyID]);

    const REPORTS_ROWS_PER_PAGE = 9;
    const totalReportPages = Math.max(1, Math.ceil(reportsList.length / REPORTS_ROWS_PER_PAGE));
    const pagedReportsList = reportsList.slice(
        (reportsListPage - 1) * REPORTS_ROWS_PER_PAGE,
        reportsListPage * REPORTS_ROWS_PER_PAGE,
    );

    useEffect(() => {
        setReportsListPage((prev) => {
            if (reportsList.length === 0) return 1;
            return prev > totalReportPages ? totalReportPages : prev;
        });
    }, [reportsList.length, totalReportPages]);

    const hydrateFormFromReportRaw = (raw) => {
        if (!raw || typeof raw !== 'object') return;
        setReportName(typeof raw.name === 'string' ? raw.name : '');
        setReportDescription(typeof raw.description === 'string' ? raw.description : '');
        setTelemetryLog(!!raw.telemetryLog);
        setOdAnalytic(!!raw.odAnalytic);
        setReportEnabled(pickReportEnabledFromRaw(raw));
        const sched = SCHEDULE_OPTIONS.find((o) => o.value === raw.schedule) || SCHEDULE_OPTIONS[0];
        setSchedule(sched);
        const dIds = Array.isArray(raw.deviceIds) ? raw.deviceIds : [];
        setSelectedDevices(deviceOptions.filter((o) => (
            dIds.includes(o.value) || (o.serial && dIds.includes(o.serial))
        )));
        const uIds = Array.isArray(raw.notifyUserIds) ? raw.notifyUserIds.map(String) : [];
        setNotifiedUsers(userOptions.filter((o) => (
            uIds.includes(String(o.value)) || uIds.includes(o.label)
        )));
        const schedKind = resolveHydrateScheduleKind(raw.schedule);
        if (schedKind === 'weekly') {
            const n = isoWeekdayFromApiWeeklyRepeat(raw.weeklyRepeatDay);
            const wOpt = WEEKLY_REPEAT_OPTIONS.find((o) => o.value === n) || WEEKLY_REPEAT_OPTIONS[0];
            setWeeklyRepeatDay(wOpt);
        } else {
            setWeeklyRepeatDay(WEEKLY_REPEAT_OPTIONS[0]);
        }
        if (schedKind === 'monthly') {
            const mk = normalizeMonthlyRepeatKeyFromApi(raw.monthlyRepeatDay);
            const mOpt = MONTHLY_REPEAT_OPTIONS.find((o) => o.value === mk) || MONTHLY_REPEAT_OPTIONS[0];
            setMonthlyRepeatDay(mOpt);
        } else {
            setMonthlyRepeatDay(MONTHLY_REPEAT_OPTIONS[0]);
        }
        if (isValidTimeHHmm(raw.sendTime)) {
            setScheduleSendTime(normalizeTimeToHour(raw.sendTime));
        } else {
            setScheduleSendTime(DEFAULT_SEND_TIME);
        }
    };

    const resetFormToDefaults = () => {
        setTelemetryLog(true);
        setOdAnalytic(false);
        setReportEnabled(true);
        setReportName('');
        setReportDescription('');
        setSelectedDevices([]);
        setSchedule(SCHEDULE_OPTIONS[0]);
        setNotifiedUsers([]);
        setWeeklyRepeatDay(WEEKLY_REPEAT_OPTIONS[0]);
        setMonthlyRepeatDay(MONTHLY_REPEAT_OPTIONS[0]);
        setScheduleSendTime(DEFAULT_SEND_TIME);
    };

    useEffect(() => {
        setNavigtionClick(6);
        setNavigationState('settings');
    }, [setNavigtionClick, setNavigationState]);

    useEffect(() => {
        if (token && agencyID) {
            fetchDevices();
            fetchTeam();
        }
    }, [token, agencyID]);

    useEffect(() => {
        if (viewMode !== 'list' || !token || !agencyID) return;
        fetchAutomateReports();
    }, [viewMode, token, agencyID, fetchAutomateReports]);

    /** Re-apply device selection when options load after opening edit (avoids empty deviceIds on save). */
    useEffect(() => {
        if (viewMode !== 'form' || !editingBaselineRaw || !devicesReady) return;
        const hydrateKey = `${reportRowId(editingBaselineRaw)}|dev:${deviceOptions.length}`;
        if (lastDeviceHydrateKeyRef.current === hydrateKey) return;
        if (deviceSelectionTouchedByUserRef.current) return;
        lastDeviceHydrateKeyRef.current = hydrateKey;
        const raw = editingBaselineRaw;
        const dIds = Array.isArray(raw.deviceIds) ? raw.deviceIds : [];
        setSelectedDevices(deviceOptions.filter((o) => (
            dIds.includes(o.value) || (o.serial && dIds.includes(o.serial))
        )));
    }, [viewMode, editingBaselineRaw, devicesReady, deviceOptions]);

    /** Re-apply notify users when team list loads after opening edit. */
    useEffect(() => {
        if (viewMode !== 'form' || !editingBaselineRaw || !teamReady) return;
        const hydrateKey = `${reportRowId(editingBaselineRaw)}|usr:${userOptions.length}`;
        if (lastNotifyHydrateKeyRef.current === hydrateKey) return;
        if (notifySelectionTouchedByUserRef.current) return;
        lastNotifyHydrateKeyRef.current = hydrateKey;
        const raw = editingBaselineRaw;
        const uIds = Array.isArray(raw.notifyUserIds) ? raw.notifyUserIds.map(String) : [];
        setNotifiedUsers(userOptions.filter((o) => (
            uIds.includes(String(o.value)) || uIds.includes(o.label)
        )));
    }, [viewMode, editingBaselineRaw, teamReady, userOptions]);

    useEffect(() => {
        if (viewMode !== 'form') {
            lastDeviceHydrateKeyRef.current = '';
            lastNotifyHydrateKeyRef.current = '';
            deviceSelectionTouchedByUserRef.current = false;
            notifySelectionTouchedByUserRef.current = false;
        }
    }, [viewMode]);

    /** POST: `telemetryLog` / `odAnalytic` at top level with the rest of the automateReport DTO. */
    const buildAutomateReportCreateBody = () => {
        const deviceIds = selectedDevices.map((o) => (
            (o.serial && String(o.serial).trim()) ? String(o.serial).trim() : String(o.value)
        ));
        const notifyUserIds = notifiedUsers.map((o) => String(o.value ?? '')).filter(Boolean);
        const sendTimeStr = normalizeTimeToHour(
            isValidTimeHHmm(scheduleSendTime) ? scheduleSendTime : DEFAULT_SEND_TIME,
        );
        const scheduleVal = schedule?.value;
        const base = {
            name: reportName.trim(),
            description: reportDescription.trim(),
            telemetryLog,
            odAnalytic,
            deviceIds,
            schedule: scheduleVal,
            notifyUserIds,
            sendTime: sendTimeStr,
            ...automateReportEnabledBodyFields(reportEnabled),
        };
        if (scheduleVal === 'monthly') {
            const mk = monthlyRepeatDay?.value ?? MONTHLY_REPEAT_OPTIONS[0].value;
            if (mk === MONTHLY_DOM_LAST) {
                base.monthlyRepeatDay = 'last';
            } else {
                const n = parseInt(String(mk), 10);
                base.monthlyRepeatDay = Number.isNaN(n) ? '1' : String(n);
            }
        } else if (scheduleVal === 'weekly') {
            base.weeklyRepeatDay = isoWeekdayToApiDayName(
                weeklyRepeatDay?.value ?? WEEKLY_REPEAT_OPTIONS[0].value,
            );
        }
        if (isMongoObjectIdString(agencyID)) {
            return { ...base, agencyOid: agencyID };
        }
        return base;
    };

    /**
     * PATCH: same shape as POST — `telemetryLog` / `odAnalytic` at top level, plus
     * `scheduleSendDate` and repeat fields as required by the API.
     */
    const buildAutomateReportPatchBody = () => {
        const deviceIds = selectedDevices.map((o) => (
            (o.serial && String(o.serial).trim()) ? String(o.serial).trim() : String(o.value)
        ));
        const notifyUserIds = notifiedUsers.map((o) => String(o.value ?? '')).filter(Boolean);
        const scheduleVal = schedule?.value;
        let scheduleSendDateStr;
        if (scheduleVal === 'weekly') {
            const iso = weeklyRepeatDay?.value ?? WEEKLY_REPEAT_OPTIONS[0].value;
            scheduleSendDateStr = nextDateStringForIsoWeekday(iso);
        } else if (scheduleVal === 'monthly') {
            const domKey = monthlyRepeatDay?.value ?? MONTHLY_REPEAT_OPTIONS[0].value;
            scheduleSendDateStr = monthlyScheduleSendDate(domKey);
        } else if (scheduleVal === 'daily') {
            scheduleSendDateStr = moment().format('YYYY-MM-DD');
        } else {
            scheduleSendDateStr = nextDateStringForIsoWeekday(WEEKLY_REPEAT_OPTIONS[0].value);
        }
        const sendTimeStr = normalizeTimeToHour(
            isValidTimeHHmm(scheduleSendTime) ? scheduleSendTime : DEFAULT_SEND_TIME,
        );
        const body = {
            name: reportName.trim(),
            description: reportDescription.trim(),
            telemetryLog,
            odAnalytic,
            deviceIds,
            schedule: scheduleVal,
            notifyUserIds,
            scheduleSendDate: scheduleSendDateStr,
            sendTime: sendTimeStr,
            ...automateReportEnabledBodyFields(reportEnabled),
        };
        if (scheduleVal === 'monthly') {
            const mk = monthlyRepeatDay?.value ?? MONTHLY_REPEAT_OPTIONS[0].value;
            if (mk === MONTHLY_DOM_LAST) {
                body.monthlyRepeatDay = 'last';
            } else {
                const n = parseInt(String(mk), 10);
                body.monthlyRepeatDay = Number.isNaN(n) ? '1' : String(n);
            }
        } else if (scheduleVal === 'weekly') {
            body.weeklyRepeatDay = isoWeekdayToApiDayName(
                weeklyRepeatDay?.value ?? WEEKLY_REPEAT_OPTIONS[0].value,
            );
        }
        return body;
    };

    const showSnackbar = (title, subtitle, action = 'success') => {
        setSnackbarTitle(title);
        setSnackbarSubtitle(subtitle);
        setSnackbarAction(action);
        setDisplaySnackbar(true);
        setTimeout(() => setDisplaySnackbar(false), 4000);
    };

    const handleSave = async (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        if (!reportName.trim()) {
            showSnackbar('Report name required', 'Please enter a name for this automated report.', 'error');
            return;
        }
        if (!token || !agencyID) {
            showSnackbar('Unable to save', 'Missing session or agency.', 'error');
            return;
        }
        if (selectedDevices.length === 0) {
            showSnackbar('Devices required', 'Select at least one device for this automated report.', 'error');
            return;
        }
        if (!telemetryLog && !odAnalytic) {
            showSnackbar('Report type required', 'Select at least one report type to include.', 'error');
            return;
        }
        setSaveSubmitting(true);
        const wasEditing = Boolean(editingReportId);
        try {
            let result;
            if (editingReportId) {
                result = await APIPatchAutomateReport(token, agencyID, editingReportId, buildAutomateReportPatchBody());
            } else {
                result = await APIPostAutomateReport(token, agencyID, buildAutomateReportCreateBody());
            }
            if (!isAutomateReportApiSuccess(result)) {
                showSnackbar('Save failed', pickApiErrorMessage(result), 'error');
                return;
            }
            setDirty(false);
            setEditingReportId(null);
            setEditingBaselineRaw(null);
            setViewMode('list');
            showSnackbar(
                'Report saved',
                wasEditing ? 'Automated report settings were updated.' : 'Automated report settings were created.',
                'success',
            );
        } catch (err) {
            showSnackbar('Save failed', err?.message || 'Please try again.', 'error');
        } finally {
            setSaveSubmitting(false);
        }
    };

    const handleReset = (e) => {
        e.preventDefault();
        if (editingBaselineRaw) {
            hydrateFormFromReportRaw(editingBaselineRaw);
        } else {
            resetFormToDefaults();
        }
        setDirty(false);
        deviceSelectionTouchedByUserRef.current = false;
        notifySelectionTouchedByUserRef.current = false;
        lastDeviceHydrateKeyRef.current = '';
        lastNotifyHydrateKeyRef.current = '';
    };

    const openCreateForm = () => {
        deviceSelectionTouchedByUserRef.current = false;
        notifySelectionTouchedByUserRef.current = false;
        lastDeviceHydrateKeyRef.current = '';
        lastNotifyHydrateKeyRef.current = '';
        setEditingReportId(null);
        setEditingBaselineRaw(null);
        resetFormToDefaults();
        setViewMode('form');
    };

    const openEditForm = (row) => {
        deviceSelectionTouchedByUserRef.current = false;
        notifySelectionTouchedByUserRef.current = false;
        lastDeviceHydrateKeyRef.current = '';
        lastNotifyHydrateKeyRef.current = '';
        setEditingReportId(row?.id ?? null);
        if (row?.raw) {
            try {
                setEditingBaselineRaw(JSON.parse(JSON.stringify(row.raw)));
            } catch (err) {
                setEditingBaselineRaw(row.raw);
            }
            hydrateFormFromReportRaw(row.raw);
        } else {
            setEditingBaselineRaw(null);
        }
        setViewMode('form');
    };

    const goBackToList = () => {
        setViewMode('list');
        setEditingReportId(null);
        setEditingBaselineRaw(null);
    };

    const openDeleteReportModal = (row) => {
        if (!row?.id) return;
        setReportPendingDelete(row);
        setDeleteReportModalOpen(true);
    };

    const closeReportMenu = () => {
        setReportMenuAnchor(null);
        setReportMenuRowId(null);
    };

    const openReportMenu = (event, row) => {
        setReportMenuAnchor(event.currentTarget);
        setReportMenuRowId(row.id);
    };

    const handleReportMenuEdit = (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        const row = reportsList.find((r) => r.id === reportMenuRowId);
        closeReportMenu();
        if (row) openEditForm(row);
    };

    const handleReportMenuDelete = (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        const row = reportsList.find((r) => r.id === reportMenuRowId);
        closeReportMenu();
        if (row) openDeleteReportModal(row);
    };

    const confirmDeleteAutomateReport = async () => {
        const row = reportPendingDelete;
        if (!row?.id || !token || !agencyID) {
            setDeleteReportModalOpen(false);
            return;
        }
        setDeleteReportModalOpen(false);
        setReportsListLoading(true);
        try {
            const result = await APIDeleteAutomateReport(token, agencyID, row.id);
            if (!isAutomateReportApiSuccess(result)) {
                showSnackbar('Delete failed', pickApiErrorMessage(result), 'error');
                return;
            }
            showSnackbar('Report deleted', 'The automated report was removed.', 'success');
            fetchAutomateReports();
        } catch (err) {
            showSnackbar('Delete failed', err?.message || 'Please try again.', 'error');
        } finally {
            setReportsListLoading(false);
        }
    };

    const renderScheduleRepeatControls = () => {
        const v = schedule?.value;
        if (v === 'weekly') {
            return (
                <div className="reports-schedule-repeat-row">
                    <span className="reports-schedule-repeat-lead">Repeat every:</span>
                    <Select
                      options={WEEKLY_REPEAT_OPTIONS}
                      value={weeklyRepeatDay}
                      onChange={(opt) => {
                          setWeeklyRepeatDay(opt || WEEKLY_REPEAT_OPTIONS[0]);
                          setDirty(true);
                      }}
                      getOptionValue={(o) => o.value}
                      getOptionLabel={(o) => o.label}
                      styles={selectStyles}
                      classNamePrefix="report-select"
                      isSearchable={false}
                      className="reports-schedule-repeat-select reports-schedule-day-select"
                    />
                    <span className="reports-schedule-repeat-mid">at</span>
                    <Select
                      options={HOURLY_TIME_OPTIONS}
                      value={
                          HOURLY_TIME_OPTIONS.find(
                              (o) => o.value === normalizeTimeToHour(scheduleSendTime),
                          ) || HOURLY_TIME_OPTIONS[9]
                      }
                      onChange={(opt) => {
                          setScheduleSendTime(opt?.value || DEFAULT_SEND_TIME);
                          setDirty(true);
                      }}
                      styles={selectStyles}
                      classNamePrefix="report-select"
                      isSearchable={false}
                      className="reports-schedule-repeat-select reports-schedule-time-select"
                    />
                </div>
            );
        }
        if (v === 'monthly') {
            return (
                <div className="reports-schedule-repeat-row">
                    <span className="reports-schedule-repeat-lead">Repeat on:</span>
                    <Select
                      options={MONTHLY_REPEAT_OPTIONS}
                      value={monthlyRepeatDay}
                      onChange={(opt) => {
                          setMonthlyRepeatDay(opt || MONTHLY_REPEAT_OPTIONS[0]);
                          setDirty(true);
                      }}
                      getOptionValue={(o) => o.value}
                      getOptionLabel={(o) => o.label}
                      styles={selectStyles}
                      classNamePrefix="report-select"
                      isSearchable={false}
                      className="reports-schedule-repeat-select reports-schedule-day-select"
                    />
                    <span className="reports-schedule-repeat-mid">at</span>
                    <Select
                      options={HOURLY_TIME_OPTIONS}
                      value={
                          HOURLY_TIME_OPTIONS.find(
                              (o) => o.value === normalizeTimeToHour(scheduleSendTime),
                          ) || HOURLY_TIME_OPTIONS[9]
                      }
                      onChange={(opt) => {
                          setScheduleSendTime(opt?.value || DEFAULT_SEND_TIME);
                          setDirty(true);
                      }}
                      styles={selectStyles}
                      classNamePrefix="report-select"
                      isSearchable={false}
                      className="reports-schedule-repeat-select reports-schedule-time-select"
                    />
                </div>
            );
        }
        return (
            <div className="reports-schedule-repeat-row">
                <span className="reports-schedule-repeat-lead">Send at:</span>
                <Select
                  options={HOURLY_TIME_OPTIONS}
                  value={
                      HOURLY_TIME_OPTIONS.find(
                          (o) => o.value === normalizeTimeToHour(scheduleSendTime),
                      ) || HOURLY_TIME_OPTIONS[9]
                  }
                  onChange={(opt) => {
                      setScheduleSendTime(opt?.value || DEFAULT_SEND_TIME);
                      setDirty(true);
                  }}
                  styles={selectStyles}
                  classNamePrefix="report-select"
                  isSearchable={false}
                  className="reports-schedule-repeat-select reports-schedule-time-select"
                />
            </div>
        );
    };

    const renderReportsListBody = () => {
        if (reportsListLoading) {
            return (
                <div className="reports-list-empty reports-list-loading">
                    Loading automated reports…
                </div>
            );
        }
        if (reportsList.length > 0) {
            return (
                <>
                    <div className="reports-list-table-and-pager">
                        <div className="reports-list-table-scroll">
                            <table className="reports-list-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Report Types</th>
                                        <th>Schedule</th>
                                        <th>Notified Users</th>
                                        <th>Last Updated</th>
                                        <th className="reports-actions-col" scope="col" aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedReportsList.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.name}</td>
                                            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- static chips, not form controls */}
                                            <td><ReportTypesChips labels={row.reportTypeTags} /></td>
                                            <td>{row.schedule}</td>
                                            <td>{row.notifiedUsers}</td>
                                            <td>{row.updatedAt}</td>
                                            <td className="reports-actions-cell">
                                                <button
                                                  type="button"
                                                  className="reports-row-menu-trigger"
                                                  aria-label="Row actions"
                                                  onClick={(ev) => openReportMenu(ev, row)}
                                                >
                                                    <span className="assets-menu">
                                                        <img loading="lazy" src={VerticalDots} alt="" />
                                                    </span>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="reports-pagination-footer">
                            <span className="reports-pagination-text">
                                {`Page ${reportsListPage} of ${totalReportPages}`}
                            </span>
                            <div className="reports-pagination-actions">
                                <PreviousButton
                                  label="Previous"
                                  onClickFunction={() => setReportsListPage((prev) => Math.max(1, prev - 1))}
                                  disable={reportsListPage === 1}
                                />
                                <NextButton
                                  label="Next"
                                  onClickFunction={() => setReportsListPage((prev) => Math.min(totalReportPages, prev + 1))}
                                  disable={reportsListPage === totalReportPages}
                                />
                            </div>
                        </div>
                    </div>
                    <Popover
                      id={reportMenuRowId ? `automated-report-menu-${reportMenuRowId}` : 'automated-report-menu'}
                      sx={{ boxShadow: 2, borderRadius: 2 }}
                      anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                      transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                      anchorEl={reportMenuAnchor}
                      open={Boolean(reportMenuAnchor)}
                      onClose={closeReportMenu}
                    >
                        <MenuPopup
                          page="automatedReports"
                          rowData={reportsList.find((r) => r.id === reportMenuRowId) || {}}
                          editButton={handleReportMenuEdit}
                          deleteButton={handleReportMenuDelete}
                        />
                    </Popover>
                </>
            );
        }
        return (
            <div className="reports-list-empty">
                No automated report has been created yet.
            </div>
        );
    };

    return (
        <div className={`notifications-container reports-automation-page${viewMode === 'list' ? ' reports-page-list' : ' reports-page-form'}`}>
            <div className="notifications-header-section">
                <div className="notifications-title">
                    <Title title="Settings" titleSize="1.875rem" lineHeight="2.375rem" />
                </div>
                <div className="notifications-tabs">
                    <ConfigTab
                      color="#FCFCFD"
                      module="settings"
                      types={navigationList}
                      users={totalUsers}
                      navigtionClick={navigtionClick}
                      setNavigtionClick={setNavigtionClick}
                    />
                </div>
            </div>

            {viewMode === 'list' && (
                <>
                    <div className="reports-list-header">
                        <h2 className="reports-list-title">Automated Reports</h2>
                        <p className="reports-list-subtitle">
                            Manage report schedules, recipients, and report types for automated exports.
                        </p>
                    </div>
                    <div className="reports-list-body">
                        <div className="reports-list-body-filter">
                            <CreateWithIcon
                              label="New Automated Report"
                              module="policy"
                              buttonHeight="44px"
                              onClickFunction={openCreateForm}
                            />
                        </div>
                        <div className={reportsListLoading ? 'reports-list-body-text-loading' : 'reports-list-body-text'}>
                            {renderReportsListBody()}
                        </div>
                    </div>
                </>
            )}

            {viewMode === 'form' && (
                <div className="notifications-column reports-notifications-column">
                            <div className="reports-back-button">
                                <BackButton label="Back to automated reports" btnClickBack={goBackToList} color="#FCFCFD" />
                            </div>
                            <div className="reports-automation-top">
                                <div className="notifications-column-header">
                                    <div className="notifications-main-text">
                                        {editingReportId ? 'Edit Automated Report' : 'New Automated Report'}
                                    </div>
                                    <div className="notifications-supporting-text">
                                        Configure report types, target devices, schedule, and recipients for this automated report.
                                    </div>
                                </div>
                            </div>
                            <ColoredLine />
                            <div className="notifications-main-body reports-automation-body">
                        <div className="reports-settings-strip reports-settings-strip-first">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Report name
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        Name this automated report to identify it in the list.
                                    </div>
                                </div>
                                <div className="reports-row-control">
                                    <input
                                      type="text"
                                      value={reportName}
                                      className="reports-text-input"
                                      maxLength={120}
                                      onChange={(e) => {
                                          setReportName(e.target.value);
                                          setDirty(true);
                                      }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="reports-settings-strip">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Report description
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        Add a short description for internal usage and report intent.
                                    </div>
                                </div>
                                <div className="reports-row-control">
                                    <textarea
                                      value={reportDescription}
                                      className="reports-text-area"
                                      maxLength={300}
                                      rows={3}
                                      onChange={(e) => {
                                          setReportDescription(e.target.value);
                                          setDirty(true);
                                      }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="reports-settings-strip">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Report
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        Choose which automated report types to include in your schedule.
                                    </div>
                                </div>
                                <div className="reports-row-control reports-action-list">
                                    <div className="reports-action-item">
                                        <div className="reports-action-checkbox">
                                            <Checkbox
                                              width="16px"
                                              height="16px"
                                              borderRadius="4px"
                                              name="telemetryLog"
                                              value="report-cb-telemetry"
                                              checked={telemetryLog}
                                              onClick={() => {
                                                  setTelemetryLog((prev) => !prev);
                                                  setDirty(true);
                                              }}
                                            />
                                        </div>
                                        <div className="reports-action-text">
                                            <div className="reports-action-title">Telemetry Log</div>
                                            <div className="reports-action-subtitle">
                                            Reports and insights to monitor and analyse asset / device performance.
                                            </div>
                                        </div>
                                    </div>

                                    <div className="reports-action-item">
                                        <div className="reports-action-checkbox">
                                            <Checkbox
                                              width="16px"
                                              height="16px"
                                              borderRadius="4px"
                                              name="odAnalytic"
                                              value="report-cb-od"
                                              checked={odAnalytic}
                                              onClick={() => {
                                                  setOdAnalytic((prev) => !prev);
                                                  setDirty(true);
                                              }}
                                            />
                                        </div>
                                        <div className="reports-action-text">
                                            <div className="reports-action-title">O/D Analytic</div>
                                            <div className="reports-action-subtitle">
                                                Reports and insights to monitor and analyse origin / destination (O/D) movement patterns of assets / devices.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="reports-settings-strip">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Select device
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        Choose one or more assets to include when automated reports are generated.
                                    </div>
                                </div>
                                <div className="reports-row-control">
                                    <Select
                                      isMulti
                                      isLoading={!devicesReady}
                                      options={deviceOptions}
                                      value={selectedDevices}
                                      onChange={(v) => {
                                          deviceSelectionTouchedByUserRef.current = true;
                                          setSelectedDevices(v || []);
                                          setDirty(true);
                                      }}
                                      placeholder="Search or select devices"
                                      styles={selectStyles}
                                      classNamePrefix="report-select"
                                      noOptionsMessage={() => 'No devices found'}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="reports-settings-strip">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Schedule report
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        How often automated reports should be prepared (daily, weekly, or monthly).
                                    </div>
                                </div>
                                <div className="reports-row-control reports-row-control-schedule">
                                    <Select
                                      options={SCHEDULE_OPTIONS}
                                      value={schedule}
                                      onChange={(opt) => {
                                          setSchedule(opt || SCHEDULE_OPTIONS[0]);
                                          setDirty(true);
                                      }}
                                      styles={selectStyles}
                                      classNamePrefix="report-select"
                                      isSearchable={false}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="reports-settings-strip">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Schedule time to send
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        {getScheduleTimeSupportingText(schedule?.value)}
                                    </div>
                                </div>
                                <div className="reports-row-control reports-row-control-schedule-repeat">
                                    {renderScheduleRepeatControls()}
                                </div>
                            </div>
                        </div>

                        <div className="reports-settings-strip">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Notified user
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        Team members who should receive an email or in-app notice when reports are sent.
                                    </div>
                                </div>
                                <div className="reports-row-control">
                                    <Select
                                      isMulti
                                      isLoading={!teamReady}
                                      options={userOptions}
                                      value={notifiedUsers}
                                      onChange={(v) => {
                                          notifySelectionTouchedByUserRef.current = true;
                                          setNotifiedUsers(v || []);
                                          setDirty(true);
                                      }}
                                      placeholder="Select team members to notify"
                                      styles={selectStyles}
                                      classNamePrefix="report-select"
                                      noOptionsMessage={() => 'No team members found'}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="reports-settings-strip reports-settings-strip-last">
                            <div className="notifications-row reports-automation-row">
                                <div className="notifications-body-text">
                                    <div className="notifications-text-and-icon">
                                        <div className="notifications-body-main-text">
                                            Enable Report
                                        </div>
                                    </div>
                                    <div className="notifications-body-supporting-text">
                                        Turn this on to allow this automated report to run on schedule.
                                    </div>
                                </div>
                                <div className="reports-row-control reports-enable-control">
                                    <Checkbox
                                      width="16px"
                                      height="16px"
                                      borderRadius="4px"
                                      name="reportEnabled"
                                      value="report-enabled-form"
                                      checked={reportEnabled}
                                      onClick={() => {
                                          setReportEnabled((prev) => !prev);
                                          setDirty(true);
                                      }}
                                    />
                                    <span className="reports-enable-label">Enabled</span>
                                </div>
                            </div>
                        </div>
                        <div className="reports-form-actions-wrap">
                            <ColoredLine />
                            <div className="reports-form-actions">
                                <SecondaryButton
                                  disable={saveSubmitting}
                                  label="Reset"
                                  width="5rem"
                                  height="2.5rem"
                                  fontSize="0.875rem"
                                  onClickFunction={handleReset}
                                />
                                <PrimaryButton
                                  disable={saveSubmitting}
                                  label="Save"
                                  onClickFunction={handleSave}
                                  height="2.5rem"
                                  fontSize="0.875rem"
                                  loading={saveSubmitting}
                                />
                            </div>
                        </div>
                            </div>
                </div>
            )}

            {displaySnackbar && (
                <div className="notification">
                    <SnackBar
                      state={displaySnackbar}
                      setState={setDisplaySnackbar}
                      title={snackbarTitle}
                      subtitile={snackbarSubtitle}
                      module={snackbarAction === 'error' ? 'error' : 'Update'}
                      action={snackbarAction}
                    />
                </div>
            )}

            {deleteReportModalOpen && (
                <div className="modal-container-table-bg">
                    <HorizonModal
                      setSwitchModalShow={setDeleteReportModalOpen}
                      switchModalShow={deleteReportModalOpen}
                      btnConfirm={confirmDeleteAutomateReport}
                      module="DeleteModal"
                      source="AutomateReport"
                      dataRow={{ name: reportPendingDelete?.name }}
                    />
                </div>
            )}
        </div>
    );
};

export default Reports;
